
import { db } from './firebase';
import { 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc, 
  arrayUnion, 
  increment, 
  Timestamp, 
  collection, 
  addDoc,
  query,
  where,
  getDocs,
  serverTimestamp
} from 'firebase/firestore';
import { 
  AuraPointsData, 
  AuraPointsHistoryItem, 
  AuraPointsReason, 
  UserRating, 
  DEFAULT_ACHIEVEMENTS, 
  Achievement
} from '@/models/AuraPoints';

const INITIAL_AURA_POINTS: AuraPointsData = {
  totalPoints: 0,
  level: 1,
  ratingsReceived: 0,
  ratingsGiven: 0,
  averageRating: 0,
  lastUpdated: new Date().toISOString(),
  history: [],
  achievements: DEFAULT_ACHIEVEMENTS,
  streak: {
    currentStreak: 0,
    longestStreak: 0,
    lastActivityDate: null
  }
};

// Calculate level based on points
export const calculateLevel = (points: number): number => {
  if (points < 100) return 1;
  if (points < 300) return 2;
  if (points < 600) return 3;
  if (points < 1000) return 4;
  if (points < 2000) return 5;
  if (points < 5000) return 6;
  return 7;
};

// Get user's aura points data
export const getUserAuraPoints = async (userId: string): Promise<AuraPointsData> => {
  try {
    const docRef = doc(db, 'auraPoints', userId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const data = docSnap.data() as AuraPointsData;
      
      // Ensure all fields exist by merging with initial data
      const completeData = {
        ...INITIAL_AURA_POINTS,
        ...data,
        achievements: data.achievements || DEFAULT_ACHIEVEMENTS,
        streak: data.streak || INITIAL_AURA_POINTS.streak
      };
      
      // Update level based on current points
      completeData.level = calculateLevel(completeData.totalPoints);
      
      return completeData;
    } else {
      // If document doesn't exist, create one with initial values
      await setDoc(docRef, INITIAL_AURA_POINTS);
      return INITIAL_AURA_POINTS;
    }
  } catch (error) {
    console.error('Error fetching aura points:', error);
    return INITIAL_AURA_POINTS;
  }
};

// Add points to user and record in history
export const addAuraPoints = async (
  userId: string, 
  points: number, 
  reason: AuraPointsReason, 
  details?: string
): Promise<void> => {
  if (!userId || points === 0) return;
  
  try {
    const userDocRef = doc(db, 'auraPoints', userId);
    const userDoc = await getDoc(userDocRef);
    
    const historyItem: AuraPointsHistoryItem = {
      id: `hist_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
      timestamp: new Date().toISOString(),
      points,
      reason,
      details
    };
    
    if (!userDoc.exists()) {
      // Create new document with initial data
      const newData: AuraPointsData = {
        ...INITIAL_AURA_POINTS,
        totalPoints: points,
        level: calculateLevel(points),
        history: [historyItem],
        lastUpdated: new Date().toISOString()
      };
      await setDoc(userDocRef, newData);
    } else {
      // Update existing document
      const currentData = userDoc.data() as AuraPointsData;
      const newTotal = currentData.totalPoints + points;
      const newLevel = calculateLevel(newTotal);
      
      await updateDoc(userDocRef, {
        totalPoints: increment(points),
        level: newLevel,
        lastUpdated: new Date().toISOString(),
        history: arrayUnion(historyItem)
      });
    }
  } catch (error) {
    console.error('Error adding aura points:', error);
    throw error;
  }
};

// Add engagement points (called periodically during active chats)
export const addEngagementPoints = async (userId: string, seconds: number): Promise<void> => {
  // Calculate points: 1 point per 30 seconds of engagement
  const points = Math.floor(seconds / 30);
  if (points > 0) {
    await addAuraPoints(
      userId, 
      points, 
      AuraPointsReason.ENGAGEMENT, 
      `${seconds} seconds of active engagement`
    );
  }
};

// Submit rating for another user
export const submitUserRating = async (
  fromUserId: string, 
  toUserId: string, 
  rating: number, 
  sessionId: string
): Promise<void> => {
  try {
    // First, store the rating
    const ratingData: UserRating = {
      fromUserId,
      toUserId,
      rating,
      timestamp: new Date().toISOString(),
      sessionId
    };
    
    await addDoc(collection(db, 'userRatings'), ratingData);
    
    // Then, add points for giving a rating (1 point per rating given)
    await addAuraPoints(
      fromUserId,
      1,
      AuraPointsReason.RATING_GIVEN,
      `Rated user with ${rating} stars`
    );
    
    // Add points to the rated user based on the rating (1-5 points)
    await addAuraPoints(
      toUserId,
      rating,
      AuraPointsReason.RATING_RECEIVED,
      `Received a ${rating}-star rating`
    );
    
    // Update the average rating for the user who received the rating
    const userPointsRef = doc(db, 'auraPoints', toUserId);
    const userPointsDoc = await getDoc(userPointsRef);
    
    if (userPointsDoc.exists()) {
      const userData = userPointsDoc.data() as AuraPointsData;
      const currentTotal = userData.averageRating * userData.ratingsReceived;
      const newTotal = currentTotal + rating;
      const newCount = userData.ratingsReceived + 1;
      const newAverage = newTotal / newCount;
      
      await updateDoc(userPointsRef, {
        ratingsReceived: increment(1),
        averageRating: newAverage
      });
    } else {
      await setDoc(userPointsRef, {
        ...INITIAL_AURA_POINTS,
        ratingsReceived: 1,
        averageRating: rating,
        lastUpdated: new Date().toISOString()
      });
    }
    
    // Update ratingGiven count for the user who gave the rating
    const raterPointsRef = doc(db, 'auraPoints', fromUserId);
    await updateDoc(raterPointsRef, {
      ratingsGiven: increment(1)
    });
    
  } catch (error) {
    console.error('Error submitting rating:', error);
    throw error;
  }
};

// Check and update user achievements
export const checkAndUpdateAchievements = async (
  userId: string, 
  specificAchievementId?: string
): Promise<void> => {
  try {
    // Get current user data
    const userPointsRef = doc(db, 'auraPoints', userId);
    const userPointsDoc = await getDoc(userPointsRef);
    
    if (!userPointsDoc.exists()) return;
    
    const userData = userPointsDoc.data() as AuraPointsData;
    let achievements = userData.achievements || [...DEFAULT_ACHIEVEMENTS];
    let needsUpdate = false;
    
    // Make sure achievements array exists
    if (!achievements) {
      achievements = [...DEFAULT_ACHIEVEMENTS];
      needsUpdate = true;
    }
    
    // Check for "First Connection" achievement
    const firstChatAchievement = achievements.find(a => a.id === 'first_chat');
    if (firstChatAchievement && !firstChatAchievement.unlockedAt && userData.history && userData.history.length > 0) {
      firstChatAchievement.unlockedAt = new Date().toISOString();
      needsUpdate = true;
      
      // Award points for achievement
      await addAuraPoints(
        userId,
        firstChatAchievement.pointsAwarded,
        AuraPointsReason.ACHIEVEMENT_UNLOCKED,
        `Achievement Unlocked: ${firstChatAchievement.name}`
      );
    }
    
    // Check for "Five-Star Communicator" achievement
    const fiveStarAchievement = achievements.find(a => a.id === 'five_star');
    if (fiveStarAchievement && !fiveStarAchievement.unlockedAt) {
      // Count 5-star ratings received
      const ratingsRef = collection(db, 'userRatings');
      const q = query(ratingsRef, where('toUserId', '==', userId), where('rating', '==', 5));
      const ratingsSnapshot = await getDocs(q);
      const fiveStarCount = ratingsSnapshot.size;
      
      // Update progress
      if (fiveStarAchievement.progress) {
        fiveStarAchievement.progress.current = fiveStarCount;
      }
      
      // Unlock if target reached
      if (fiveStarCount >= 10) {
        fiveStarAchievement.unlockedAt = new Date().toISOString();
        
        // Award points for achievement
        await addAuraPoints(
          userId,
          fiveStarAchievement.pointsAwarded,
          AuraPointsReason.ACHIEVEMENT_UNLOCKED,
          `Achievement Unlocked: ${fiveStarAchievement.name}`
        );
      }
      
      needsUpdate = true;
    }
    
    // Check for "Profile Complete" achievement
    if (specificAchievementId === 'profile_complete' || !specificAchievementId) {
      const profileCompleteAchievement = achievements.find(a => a.id === 'profile_complete');
      if (profileCompleteAchievement && !profileCompleteAchievement.unlockedAt) {
        // This would need to check if the user's profile is complete
        // For now, we'll unlock it automatically when checking
        if (specificAchievementId === 'profile_complete') {
          profileCompleteAchievement.unlockedAt = new Date().toISOString();
          
          // Award points for achievement
          await addAuraPoints(
            userId,
            profileCompleteAchievement.pointsAwarded,
            AuraPointsReason.ACHIEVEMENT_UNLOCKED,
            `Achievement Unlocked: ${profileCompleteAchievement.name}`
          );
          
          needsUpdate = true;
        }
      }
    }
    
    // Chat Marathon achievement should be checked after long chat sessions
    
    // Social Butterfly achievement - chat with different people
    const socialButterflyAchievement = achievements.find(a => a.id === 'social_butterfly');
    if (socialButterflyAchievement && !socialButterflyAchievement.unlockedAt) {
      // Get unique users chatted with (based on ratings)
      const ratingsRef = collection(db, 'userRatings');
      const sentRatingsQuery = query(ratingsRef, where('fromUserId', '==', userId));
      const sentRatingsSnapshot = await getDocs(sentRatingsQuery);
      
      const uniqueUsers = new Set();
      sentRatingsSnapshot.forEach(doc => {
        const data = doc.data() as UserRating;
        uniqueUsers.add(data.toUserId);
      });
      
      const uniqueUsersCount = uniqueUsers.size;
      
      // Update progress
      if (socialButterflyAchievement.progress) {
        socialButterflyAchievement.progress.current = uniqueUsersCount;
      }
      
      // Unlock if target reached
      if (uniqueUsersCount >= 20) {
        socialButterflyAchievement.unlockedAt = new Date().toISOString();
        
        // Award points for achievement
        await addAuraPoints(
          userId,
          socialButterflyAchievement.pointsAwarded,
          AuraPointsReason.ACHIEVEMENT_UNLOCKED,
          `Achievement Unlocked: ${socialButterflyAchievement.name}`
        );
      }
      
      needsUpdate = true;
    }
    
    // Update achievements in Firestore if any changes
    if (needsUpdate) {
      await updateDoc(userPointsRef, {
        achievements: achievements,
        lastUpdated: new Date().toISOString()
      });
    }
    
  } catch (error) {
    console.error('Error checking achievements:', error);
    throw error;
  }
};

// Check and update daily streak
export const checkAndUpdateStreak = async (userId: string): Promise<void> => {
  try {
    const userPointsRef = doc(db, 'auraPoints', userId);
    const userPointsDoc = await getDoc(userPointsRef);
    
    if (!userPointsDoc.exists()) return;
    
    const userData = userPointsDoc.data() as AuraPointsData;
    const streak = userData.streak || {
      currentStreak: 0,
      longestStreak: 0,
      lastActivityDate: null
    };
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const lastActivityDate = streak.lastActivityDate 
      ? new Date(streak.lastActivityDate) 
      : null;
      
    if (lastActivityDate) {
      lastActivityDate.setHours(0, 0, 0, 0);
      
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      
      // Check if user was active yesterday or today
      if (lastActivityDate.getTime() === yesterday.getTime()) {
        // Increment streak for a new day
        streak.currentStreak += 1;
        streak.lastActivityDate = today.toISOString();
        
        // Update longest streak if current is greater
        if (streak.currentStreak > streak.longestStreak) {
          streak.longestStreak = streak.currentStreak;
        }
        
        // Check if week streak achievement is unlocked
        const achievements = userData.achievements || [...DEFAULT_ACHIEVEMENTS];
        const weekStreakAchievement = achievements.find(a => a.id === 'week_streak');
        
        if (weekStreakAchievement && !weekStreakAchievement.unlockedAt) {
          // Update progress
          if (weekStreakAchievement.progress) {
            weekStreakAchievement.progress.current = streak.currentStreak;
          }
          
          // Unlock if 7-day streak reached
          if (streak.currentStreak >= 7) {
            weekStreakAchievement.unlockedAt = new Date().toISOString();
            
            // Award points for achievement
            await addAuraPoints(
              userId,
              weekStreakAchievement.pointsAwarded,
              AuraPointsReason.ACHIEVEMENT_UNLOCKED,
              `Achievement Unlocked: ${weekStreakAchievement.name}`
            );
            
            // Also award streak bonus points
            await addAuraPoints(
              userId,
              20,
              AuraPointsReason.STREAK_BONUS,
              `${streak.currentStreak}-day streak bonus!`
            );
          }
          
          // Update achievements
          await updateDoc(userPointsRef, {
            achievements: achievements
          });
        }
        
      } else if (lastActivityDate.getTime() < yesterday.getTime()) {
        // Streak broken - more than one day since last activity
        streak.currentStreak = 1;
        streak.lastActivityDate = today.toISOString();
      } else if (lastActivityDate.getTime() !== today.getTime()) {
        // Update last activity date to today
        streak.lastActivityDate = today.toISOString();
      }
    } else {
      // First ever activity
      streak.currentStreak = 1;
      streak.lastActivityDate = today.toISOString();
    }
    
    // Update streak in Firestore
    await updateDoc(userPointsRef, {
      streak: streak,
      lastUpdated: new Date().toISOString()
    });
    
  } catch (error) {
    console.error('Error updating streak:', error);
    throw error;
  }
};

// Get average rating for a user
export const getUserAverageRating = async (userId: string): Promise<number> => {
  try {
    const docRef = doc(db, 'auraPoints', userId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const data = docSnap.data() as AuraPointsData;
      return data.averageRating;
    }
    return 0;
  } catch (error) {
    console.error('Error getting user average rating:', error);
    return 0;
  }
};
